import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authService } from '../services/auth';
import { apiClient, api } from '../services/api';

const schema = z.object({
  password: z.string()
    .min(8, 'Au moins 8 caractères')
    .regex(/[A-Z]/, 'Au moins une majuscule')
    .regex(/[a-z]/, 'Au moins une minuscule')
    .regex(/[0-9]/, 'Au moins un chiffre')
    .regex(/[!@#$%^&*]/, 'Au moins un caractère spécial (!@#$%^&*)'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

type FormData = z.infer<typeof schema>;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/config')
      .then((res) => { if (res.data?.logoUrl) setLogoUrl(res.data.logoUrl); })
      .catch(() => {});
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    if (!token) return;
    setIsSubmitting(true);
    setError(null);

    try {
      await authService.resetPassword(token, data.password);
      setSuccess(true);
    } catch (err) {
      setError(apiClient.getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, hsl(30 36% 89%) 0%, hsl(30 36% 95%) 100%)' }}>
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Univers Massages"
              className="mx-auto h-36 w-auto mb-6 rounded-2xl"
            />
          )}
          <p className="text-sm" style={{ color: 'hsl(17 47% 58%)' }}>
            Nouveau mot de passe
          </p>
        </div>

        <div className="mt-6 sm:mt-8 bg-white/80 backdrop-blur-sm px-4 sm:px-8 py-6 sm:py-10 shadow-sm rounded-lg" style={{ borderTop: '3px solid hsl(17 47% 68%)' }}>
          {!token ? (
            <div className="space-y-4">
              <div className="rounded-md bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-800">Lien invalide. Aucun token fourni.</p>
              </div>
              <Link
                to="/forgot-password"
                className="block text-center text-sm font-medium hover:underline"
                style={{ color: 'hsl(17 47% 48%)' }}
              >
                Demander un nouveau lien
              </Link>
            </div>
          ) : success ? (
            <div className="space-y-4">
              <div className="rounded-md bg-green-50 border border-green-200 p-4">
                <p className="text-sm text-green-800">
                  Mot de passe réinitialisé avec succès !
                </p>
              </div>
              <Link
                to="/login"
                className="block text-center text-sm font-medium hover:underline"
                style={{ color: 'hsl(17 47% 48%)' }}
              >
                Se connecter
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">Nouveau mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  {...register('password')}
                  className={errors.password ? 'border-red-500' : ''}
                />
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  {...register('confirmPassword')}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>

              <ul className="text-xs text-gray-500 space-y-1">
                <li>Au moins 8 caractères</li>
                <li>Une majuscule et une minuscule</li>
                <li>Un chiffre</li>
                <li>Un caractère spécial (!@#$%^&*)</li>
              </ul>

              <Button
                type="submit"
                className="w-full text-white"
                style={{ background: 'linear-gradient(135deg, hsl(17 47% 68%) 0%, hsl(17 47% 58%) 100%)' }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
